import {
  type CellState,
  type ColorId,
  type GameState,
  type Pos,
  isAdjacent,
  isInBounds,
  posEqual,
} from './types.js';

export interface ValidateMoveContext {
  size: number;
  endpoints: Map<ColorId, [Pos, Pos]>;
  grid: CellState[][];
  paths: Map<ColorId, Pos[]>;
  color: ColorId;
  pos: Pos;
}

export function getEndpointAt(
  endpoints: Map<ColorId, [Pos, Pos]>,
  pos: Pos,
  excludeColor?: ColorId,
): ColorId | null {
  for (const [color, [a, b]] of endpoints) {
    if (color === excludeColor) continue;
    if (posEqual(a, pos) || posEqual(b, pos)) return color;
  }
  return null;
}

export function validateMove(ctx: ValidateMoveContext): boolean {
  const { size, endpoints, grid, paths, color, pos } = ctx;

  if (!isInBounds(pos, size)) return false;

  const path = paths.get(color) ?? [];
  const [x, y] = pos;
  const cell = grid[y][x];

  if (path.length === 0) {
    const pair = endpoints.get(color);
    if (!pair) return false;
    return posEqual(pair[0], pos) || posEqual(pair[1], pos);
  }

  const last = path[path.length - 1];
  if (!isAdjacent(last, pos)) return false;

  if (cell === color) {
    if (path.length >= 2 && posEqual(path[path.length - 2], pos)) {
      return true;
    }
    return false;
  }

  if (cell !== null) return false;

  const otherEndpoint = getEndpointAt(endpoints, pos, color);
  if (otherEndpoint !== null) return false;

  const pair = endpoints.get(color);
  if (!pair) return false;
  const isOwnEndpoint =
    posEqual(pair[0], pos) || posEqual(pair[1], pos);
  if (isOwnEndpoint) {
    const other = posEqual(pair[0], pos) ? pair[1] : pair[0];
    const hasOtherInPath = path.some((p) => posEqual(p, other));
    if (!hasOtherInPath) return false;
  }

  return true;
}

export function validateExtendPath(
  state: GameState,
  color: ColorId,
  pos: Pos,
): boolean {
  return validateMove({
    size: state.size,
    endpoints: state.endpoints,
    grid: state.grid,
    paths: state.paths,
    color,
    pos,
  });
}
